import moreCollapsedImg from "@assets/header/more-collapsed.svg";
import moreExpandedImg from "@assets/header/more-expanded.svg";
import moreCollapsedImgNew from "@assets/header/newIcons/more-collapsed.svg";
import moreExpandedImgNew from "@assets/header/newIcons/more-expanded.svg";
import { SECTION_LIST_NEW } from "@components/Modules/HeaderNew/headerMenuConstants";
import { ENABLE_NEW_HEADER_FOOTER } from "@helper/getEnvVariables";
import { type ReactElement, useState } from "react";

import { HeaderMenuList } from "./HeaderMenuList";
import { SearchButton } from "./Search/SearchButton";
import { SearchInput } from "./Search/SearchInput";
import { SecondaryLinks } from "./SecondaryLinks";

export type SectionQuickLinksProps = Readonly<{
  testId?: string;
  hideScrollableSectionMenu?: boolean;
}>;

enum Buttons {
  MORE_BUTTON = "more",
  SEARCH_BUTTON = "search",
}

export function SectionQuickLinks({
  testId = "desktop-quick-links",
  hideScrollableSectionMenu = false,
}: SectionQuickLinksProps): ReactElement {
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [moreMenuExpanded, setMoreMenuExpanded] = useState(false);
  const toggleExpanded = (type: Buttons) => {
    if (type === Buttons.MORE_BUTTON) {
      setMoreMenuExpanded((prev) => !prev);
      setSearchExpanded(false);
    } else {
      setMoreMenuExpanded(false);
      setSearchExpanded((prev) => !prev);
    }
  };

  const newMasthead = ENABLE_NEW_HEADER_FOOTER;

  const wrapperClass = newMasthead
    ? "lg:bg-bg-secondary"
    : "lg:bg-gradient-to-r from-blue-400 to-blue-450";
  const moreButtonClass = newMasthead
    ? "text-sm lg:text-brand-primary"
    : "text-xs lg:text-text-inverse";
  const topicsListWrapperClass = newMasthead ? "lg:flex-grow-no-shrink" : "";

  const moreMenuExpandedClass = moreMenuExpanded
    ? newMasthead
      ? "bg-neutral-200"
      : "bg-blue-100"
    : "";
  const MoreExpandedIcon = newMasthead ? moreExpandedImgNew : moreExpandedImg;
  const MoreCollapsedIcon = newMasthead
    ? moreCollapsedImgNew
    : moreCollapsedImg;

  const secondaryLinksClass = newMasthead
    ? "bg-neutral-200"
    : "bg-gradient-to-r from-blue-400 to-blue-100";

  return (
    <>
      <div
        data-testid={`${testId}-quickLinks`}
        className={`flex justify-center text-sm font-medium ${wrapperClass}`}
      >
        <div
          data-testid={`${testId}-quickLinksInner`}
          className="flex w-full lg:max-w-screen-lg xl:max-w-screen-bh-cxl"
        >
          <div
            data-testid={`${testId}-topicsListWrapper`}
            className="h-full flex w-full justify-between border-b-white-200 border-b-[1px] lg:border-b-0"
          >
            <div
              className={`h-[40px] lg:h-auto lg:gap-xs xl:gap-md px-xs xl:px-sm
               overflow-x-scroll lg:overflow-visible
               ${hideScrollableSectionMenu ? "hidden lg:flex" : "flex"}
               ${topicsListWrapperClass}
               `}
            >
              <HeaderMenuList
                testId={testId}
                items={SECTION_LIST_NEW}
                type="topics"
              />
              <div
                className={`hidden lg:flex items-center lg:px-xs xl:px-sm ${moreMenuExpandedClass}`}
              >
                <button
                  className={`flex items-center gap-1 ${moreButtonClass}`}
                  onClick={() => {
                    toggleExpanded(Buttons.MORE_BUTTON);
                  }}
                >
                  More
                  <img
                    src={
                      moreMenuExpanded ? MoreExpandedIcon : MoreCollapsedIcon
                    }
                    alt="more-menu-icon"
                    width={12}
                    height={12}
                  />
                </button>
              </div>
            </div>
            <div
              data-testid={`${testId}-headerSearch`}
              className="hidden lg:block"
            >
              <SearchButton
                expanded={searchExpanded}
                onClick={() => {
                  toggleExpanded(Buttons.SEARCH_BUTTON);
                }}
                variant={newMasthead ? "blue" : "light"}
              />
            </div>
          </div>
        </div>
      </div>
      <div
        data-testid={`${testId}-secondaryNav`}
        className={`hidden lg:flex justify-center ${secondaryLinksClass} transition-all duration-500 ease-in-out
            ${moreMenuExpanded ? "opacity-100 h-[35px] visible" : "opacity-0 h-0 invisible"}
          `}
      >
        <SecondaryLinks testId={testId} />
      </div>
      <div
        data-testid={`${testId}-searchInputDesktop`}
        className={`hidden lg:flex justify-center bg-neutral-200 transition-all duration-500 ease-in-out
            ${searchExpanded ? "opacity-100 h-[35px] visible" : "opacity-0 h-0 invisible"}
          `}
      >
        <SearchInput expanded={searchExpanded} />
      </div>
    </>
  );
}
